import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Avatar,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Label from "../../Common/Label";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "../../../assets/images/deleteIcon.svg";
import { getRequest, postRequest } from "../../../api/apiServices";
import apiList, { getCompany } from "../../../api/apiList";
import { useAuth } from "../../../hooks/useAuth";
import ReactQuill from "react-quill";
import {
  currencies,
  periods,
  employmentTypes,
  educationLevels,
  updateCampaignFormData,
} from "../../../constant/VonqConstant";
import Loading from "../UI/Loading";
import Error from "../UI/ErrorMessage";

interface Form6Props {
  nextStep: () => void;
  prevStep: () => void;
}

interface Education {
  id: number;
  name: string;
}

interface Seniority {
  id: number;
  name: string;
}

interface JobFunction {
  id: number;
  name: string;
}

interface Industry {
  id: number;
  name: string;
}

export interface JobCampaignFormData {
  Form1Data?: {
    jobTitle?: { id: number; name: string };
    jobFunction?: { id: number; name: string };
    location?: { id: number; name: string };
    industries?: { id: number; name: string };
  };
  selectedCampaigns?: any[];
  selectedCampaign?: any[];
  Form2Data?: {
    filterBy?: string;
    sortBy?: string;
    channelData?: any;
    selectedCampaigns?: any[];
  };
  Form3Data?: {
    education?: { id: number; name: string };
    seniority?: { id: number; name: string };
    jobFunction?: { id: number; name: string };
    industry?: { id: number; name: string };
  };
  Form4Data?: {
    recruiterFullName?: string;
    recruiterEmail?: string;
    contactFullName?: string;
    contactEmail?: string;
    contactPhone?: string;
  };
  Form5Data?: {
    campaignData?: {
      campaignName?: string;
      jobTitle?: string;
      jobDescription?: string;
      yearsOfExperience?: number;
      employmentType?: string;
      fromWorkingHours?: string;
      fromWorkingMinutes?: string;
      toWorkingHours?: string;
      toWorkingMinutes?: string;
      salaryIndicationFrom?: string;
      salaryIndicationTo?: string;
      currency?: string;
      period?: string;
    };
    workingLocation?: {
      addressLine1?: string;
      addressLine2?: string;
      postCode?: string;
      city?: string;
      country?: string;
      allowsRemoteWork?: boolean;
    };
  };
}

const Form6: React.FC<Form6Props> = ({ nextStep, prevStep }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>("");
  const isMobile = useMediaQuery("(max-width:578px)");

  // API Data States
  const [education, setEducation] = useState<Education[]>([]);
  const [seniority, setSeniority] = useState<Seniority[]>([]);
  const [jobFunctions, setJobFunctions] = useState<JobFunction[]>([]);
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [companyInfoData, setCompanyInfoData] = useState<{
    companyName: string;
    companyLogo: string;
    companyId: number;
  }>({
    companyName: "",
    companyLogo: "",
    companyId: 0,
  });

  const [campaignFormState, setcampaignFormState] =
    useState<JobCampaignFormData>({});

  const { user } = useAuth();

  const [campaignDisplayState, setCampaignDisplayState] = useState({
    // Target Group
    selectedEducation: null as Education | null,
    selectedSeniority: null as Seniority | null,
    selectedJobFunction: null as JobFunction | null,
    selectedIndustry: null as Industry | null,
    selectedCampaigns: [] as any[],

    // Recruiter Info
    recruiterFullName: "",
    recruiterEmail: "",

    // Contact Info
    contactFullName: "",
    contactEmail: "",
    contactPhone: "",

    // Campaign Details
    campaignName: "",
    jobTitle: "",
    jobDescription: "",
    yearsOfExperience: 0,
    employmentType: "",
    fromWorkingHours: "",
    fromWorkingMinutes: "",
    toWorkingHours: "",
    toWorkingMinutes: "",
    salaryIndicationFrom: "",
    salaryIndicationTo: "",
    currency: "",
    period: "",

    // Working Location
    addressLine1: "",
    addressLine2: "",
    postCode: "",
    city: "",
    country: "",
    allowsRemoteWork: false,
  });

  const getCompanyData = async () => {
    try {
      const response: any = await getRequest(getCompany(user?.uId));
      if (response?.data) {
        setCompanyInfoData({
          companyName: response?.data?.data?.name,
          companyLogo: response?.data?.data?.logo,
          companyId: response?.data?.data?.id,
        });
      }
    } catch (error) {
      setError("Error in getCompanyData: " + error);
    }
  };

  // Load data from localStorage
  const loadLocalStorageData = (): JobCampaignFormData | undefined => {
    try {
      const storedData = localStorage.getItem("CompaignData");
      if (!storedData) return;

      const parsedData: JobCampaignFormData = JSON.parse(storedData);
      setcampaignFormState(parsedData);
      return parsedData;
    } catch (error) {
      setError("Error loading saved data");
    }
  };

  // Fetch all required API data
  const fetchApiData = async () => {
    setLoading(true);
    setError("");

    try {
      const [educationRes, seniorityRes, jobFunctionRes, industryRes] =
        await Promise.all([
          getRequest(apiList.getEducation),
          getRequest(apiList.getSeniority),
          getRequest(apiList.getJobFunction),
          getRequest(apiList.getIndustries),
        ]);

      // Process Education
      if (educationRes?.data?.success) {
        setEducation(educationRes.data.data || []);
      }

      // Process Seniority
      if (seniorityRes?.data?.success) {
        setSeniority(seniorityRes.data.data || []);
      }

      // Process Job Functions
      if (jobFunctionRes?.data?.success) {
        setJobFunctions(jobFunctionRes.data.data || []);
      }

      // Process Industries
      if (industryRes?.data?.success) {
        setIndustries(industryRes.data.data || []);
      }
    } catch (error) {
      setError("Error loading dropdown options");
    } finally {
      setLoading(false);
    }
  };

  // Map localStorage data to display format
  const buildCampaignDisplayState = (
    localData: JobCampaignFormData,
    apiData: {
      education: Education[];
      seniority: Seniority[];
      jobFunctions: JobFunction[];
      industries: Industry[];
      selectedCampaigns: any[];
    }
  ) => {
    const newDisplayData = { ...campaignDisplayState };

    const {
      Form1Data = {},
      Form2Data = {},
      Form3Data = {},
      Form4Data = {},
      Form5Data = {},
      selectedCampaigns: rootSelectedCampaigns,
      selectedCampaign: rootSelectedCampaign,
    } = localData;

    // --- Target Group (priority: Form3Data → Form1Data) ---
    newDisplayData.selectedEducation = Form3Data.education ?? null;
    newDisplayData.selectedSeniority = Form3Data.seniority ?? null;
    newDisplayData.selectedJobFunction =
      Form3Data.jobFunction ?? Form1Data.jobFunction ?? null;
    newDisplayData.selectedIndustry =
      Form3Data.industry ?? Form1Data.industries ?? null;

    // --- Recruiter & Contact Info ---
    newDisplayData.recruiterFullName = Form4Data.recruiterFullName ?? "";
    newDisplayData.recruiterEmail = Form4Data.recruiterEmail ?? "";
    newDisplayData.contactFullName = Form4Data.contactFullName ?? "";
    newDisplayData.contactEmail = Form4Data.contactEmail ?? "";
    newDisplayData.contactPhone = Form4Data.contactPhone ?? "";

    // --- Campaign Details ---
    const campaignData = Form5Data.campaignData ?? {};
    newDisplayData.campaignName = campaignData.campaignName ?? "";
    newDisplayData.jobTitle =
      campaignData.jobTitle ?? Form1Data?.jobTitle?.name ?? "";
    newDisplayData.jobDescription = campaignData.jobDescription ?? "";
    newDisplayData.yearsOfExperience = campaignData.yearsOfExperience ?? 0;
    newDisplayData.employmentType = campaignData.employmentType ?? "";
    newDisplayData.fromWorkingHours = campaignData.fromWorkingHours ?? "";
    newDisplayData.fromWorkingMinutes = campaignData.fromWorkingMinutes ?? "";
    newDisplayData.toWorkingHours = campaignData.toWorkingHours ?? "";
    newDisplayData.toWorkingMinutes = campaignData.toWorkingMinutes ?? "";
    newDisplayData.salaryIndicationFrom =
      campaignData.salaryIndicationFrom ?? "";
    // Preserve user-entered value; use empty string when missing so payload can send nulls
    newDisplayData.salaryIndicationTo = campaignData.salaryIndicationTo ?? "";
    newDisplayData.currency = campaignData.currency ?? "";
    newDisplayData.period = campaignData.period ?? "";

    // --- Working Location ---
    const workingLocation = Form5Data.workingLocation ?? {};
    newDisplayData.addressLine1 = workingLocation.addressLine1 ?? "";
    newDisplayData.addressLine2 = workingLocation.addressLine2 ?? "";
    newDisplayData.postCode = workingLocation.postCode ?? "";
    newDisplayData.city = workingLocation.city ?? "";
    newDisplayData.country = workingLocation.country ?? "";
    newDisplayData.allowsRemoteWork = workingLocation.allowsRemoteWork ?? false;

    // --- Selected Campaigns (priority: root → Form2 → apiData) ---
    const selectedCampaigns =
      Array.isArray(rootSelectedCampaigns) && rootSelectedCampaigns.length
        ? rootSelectedCampaigns
        : Array.isArray(rootSelectedCampaign) && rootSelectedCampaign.length
        ? rootSelectedCampaign
        : Array.isArray(Form2Data.selectedCampaigns) &&
          Form2Data.selectedCampaigns.length
        ? Form2Data.selectedCampaigns
        : Array.isArray(apiData.selectedCampaigns) &&
          apiData.selectedCampaigns.length
        ? apiData.selectedCampaigns
        : [];

    newDisplayData.selectedCampaigns = selectedCampaigns.filter(
      (c) => c && (c.productId || c.id)
    );

    setCampaignDisplayState(newDisplayData);
  };

  // Handle changes and update localStorage
  const handleChange = (
    fieldName: keyof typeof campaignDisplayState,
    newValue: any
  ) => {
    if (
      [
        "fromWorkingHours",
        "fromWorkingMinutes",
        "toWorkingHours",
        "toWorkingMinutes",
      ].includes(fieldName as string)
    )
      newValue = Math.min(Number(newValue || 0), 168);
    const newFormData: JobCampaignFormData = { ...campaignFormState };
    const updatedFormData = updateCampaignFormData(
      campaignFormState,
      fieldName,
      newValue
    );
    setcampaignFormState(newFormData);
    localStorage.setItem("CompaignData", JSON.stringify(newFormData));
  };
  // Initialize component
  useEffect(() => {
    loadLocalStorageData();
    fetchApiData();
    getCompanyData();
  }, []);

  // Map data when both localStorage and API data are available
  useEffect(() => {
    if (!loading && Object.keys(campaignFormState).length > 0) {
      buildCampaignDisplayState(campaignFormState, {
        education,
        seniority,
        jobFunctions,
        industries,
        selectedCampaigns:
          campaignFormState.selectedCampaigns ||
          campaignFormState.selectedCampaign ||
          campaignFormState.Form2Data?.selectedCampaigns ||
          [],
      });
    }
  }, [
    loading,
    campaignFormState,
    education,
    seniority,
    jobFunctions,
    industries,
  ]);

  // Remove a selected campaign and persist
  const removeSelectedCampaign = (productId: string) => {
    const updated = { ...campaignFormState } as JobCampaignFormData;

    // Helper to remove a productId from an array
    const removeFromArray = (arr: any[] | undefined) =>
      Array.isArray(arr)
        ? arr.filter((c: any) => (c?.productId || c?.id) !== productId)
        : [];

    // Remove from all possible places
    updated.selectedCampaigns = removeFromArray(updated.selectedCampaigns);
    updated.selectedCampaign = removeFromArray(updated.selectedCampaign);
    if (updated.Form2Data?.selectedCampaigns) {
      updated.Form2Data.selectedCampaigns = removeFromArray(
        updated.Form2Data.selectedCampaigns
      );
    }

    // Update state and localStorage
    setcampaignFormState(updated);
    setCampaignDisplayState((prev) => ({
      ...prev,
      selectedCampaigns:
        updated.selectedCampaigns ||
        updated.selectedCampaign ||
        updated.Form2Data?.selectedCampaigns ||
        [],
    }));
    localStorage.setItem("CompaignData", JSON.stringify(updated));
  };

  // Build payload for validate-payload
  const buildCampaignPayload = () => {
    const {
      selectedCampaigns = [],
      contactFullName = "",
      contactEmail = "",
      contactPhone = "",
      addressLine1 = "",
      postCode = "",
      city = "",
      country = "",
      allowsRemoteWork = false,
      selectedEducation = null,
      selectedSeniority = null,
      selectedIndustry = null,
      selectedJobFunction = null,
      campaignName = "Test Campaign",
      recruiterFullName = "",
      jobTitle = "",
      jobDescription = "",
      employmentType = "",
    } = campaignDisplayState;

    // Helper to map selected target items
    const mapSelected = (item: any) =>
      item ? [{ description: item.name, vonqId: item.id }] : [];

    const salaryFromRaw = campaignDisplayState.salaryIndicationFrom;
    const salaryToRaw = campaignDisplayState.salaryIndicationTo;
    const currencyRaw = campaignDisplayState.currency;
    const periodRaw = campaignDisplayState.period;

    const parseOptionalNumber = (value: any) => {
      if (value === undefined || value === null) return null;
      const trimmed = String(value).trim();
      if (!trimmed) return null;
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed === 0) return null;
      return parsed;
    };

    const parsedSalaryFrom = parseOptionalNumber(salaryFromRaw);
    const parsedSalaryTo = parseOptionalNumber(salaryToRaw);
    const parsedCurrency =
      currencyRaw && String(currencyRaw).trim() !== "" ? currencyRaw : null;
    const parsedPeriod =
      periodRaw && String(periodRaw).trim() !== "" ? periodRaw : null;

    const salaryIndicationPayload = {
      period: parsedPeriod,
      range: {
        from: parsedSalaryFrom,
        to: parsedSalaryTo,
        currency: parsedCurrency,
      },
    };

    const payload = {
      campaignPayload: {
        labels: null,
        companyId: companyInfoData?.companyId ?? 0,
        campaignName,
        recruiterInfo: { name: recruiterFullName },
        postingDetails: {
          title: jobTitle,
          description: jobDescription,
          organization: {
            name: companyInfoData?.companyName ?? "",
            companyLogo: companyInfoData?.companyLogo ?? "",
          },
          contactInfo: {
            name: contactFullName,
            emailAddress: contactEmail,
            phoneNumber: contactPhone,
          },
          workingLocation: {
            addressLine1,
            postcode: postCode,
            city,
            country,
            allowsRemoteWork: allowsRemoteWork ? 1 : 0,
          },
          employmentType,
          salaryIndication: salaryIndicationPayload,
        },
        targetGroup: {
          educationLevel: mapSelected(selectedEducation),
          seniority: mapSelected(selectedSeniority),
          industry: mapSelected(selectedIndustry),
          jobCategory: mapSelected(selectedJobFunction),
        },
        orderedProducts: selectedCampaigns
          .map((c: any) => c?.productId)
          .filter(Boolean),
        orderedProductsSpecs: selectedCampaigns
          .map(
            (c: any) => c?.productId && { productId: c.productId, utm: c?.utm }
          )
          .filter(Boolean),
        poNumber: null,
      },
    } as any;

    return payload;
  };

  const handleSubmitValidatePayload = async () => {
    try {
      setSubmitting(true);
      nextStep();
    } catch (e) {
      setError("Failed to submit campaign payload");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loading />;

  if (error)
    return (
      <Error
        error={error}
        onRetry={() => {
          setError("");
          setLoading(true);
          fetchApiData();
        }}
      />
    );

  return (
    <div className="container" id="campaign-container">
      <div className="col d-flex align-items-start justify-content-center flex-column gap-1">
        <div className="d-flex">
          <Avatar className="avatar">6</Avatar>
          <Label
            children="Order Review"
            fontSize={"20.94px"}
            fontWeight={700}
            color="#273848"
          />
        </div>
        <Label
          children="Review your campaign details before ordering."
          fontWeight={500}
          fontSize={"12.94px"}
          color="#273848"
        />
      </div>

      <div className="col mt-4">
        {/* Basket Summary Accordion */}
        <Accordion className="accordion" defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
            className="accordion-header"
          >
            <Typography component="span" className="accortion-header-title">
              Basket Summary
            </Typography>
          </AccordionSummary>
          <AccordionDetails className="accordion-details">
            <div className="col mt-2">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Label
                  children={`Basket: ${campaignDisplayState.selectedCampaigns.length} channels`}
                  fontWeight={500}
                  fontSize={"14px"}
                  color="#4E4E4E"
                />
              </div>

              {campaignDisplayState.selectedCampaigns.length === 0 ? (
                <div className="text-muted mt-2">No channels selected</div>
              ) : (
                <div className="w-100">
                  {/* Header Row */}
                  <div className="row px-2 py-2 border-bottom d-none d-md-flex">
                    <div className="col-4">
                      <Label
                        children="Channel"
                        fontWeight={600}
                        fontSize="13px"
                        color="#666"
                      />
                    </div>
                    <div className="col-3">
                      <Label
                        children="Duration"
                        fontWeight={600}
                        fontSize="13px"
                        color="#666"
                      />
                    </div>
                    <div className="col-3">
                      <Label
                        children="Processing Time"
                        fontWeight={600}
                        fontSize="13px"
                        color="#666"
                      />
                    </div>
                    <div className="col-1 text-end">
                      <Label
                        children="Price"
                        fontWeight={600}
                        fontSize="13px"
                        color="#666"
                      />
                    </div>
                    <div className="col-1"></div>
                  </div>

                  {/* Items */}
                  {campaignDisplayState.selectedCampaigns.map((c: any) => (
                    <div
                      key={c.productId}
                      className="row px-2 py-3 border-bottom align-items-center"
                    >
                      <div className="col-12 col-md-4 mb-2 mb-md-0">
                        <Label
                          children={c.channelName || c.channelType || "Channel"}
                          fontWeight={500}
                          fontSize={"14px"}
                          color="#273848"
                        />
                      </div>
                      <div className="col-6 col-md-3">
                        <span className="d-md-none text-muted small me-2">
                          Duration:
                        </span>
                        <Label
                          children={c.duration || "-"}
                          fontWeight={400}
                          fontSize={"14px"}
                          color="#4E4E4E"
                        />
                      </div>
                      <div className="col-6 col-md-3">
                        <span className="d-md-none text-muted small me-2">
                          Processing:
                        </span>
                        <Label
                          children={`${c.processingTime || "-"}`}
                          fontWeight={400}
                          fontSize={"14px"}
                          color="#4E4E4E"
                        />
                      </div>
                      <div className="col-6 col-md-1 text-md-end mt-2 mt-md-0">
                        <span className="d-md-none text-muted small me-2">
                          Price:
                        </span>
                        <Label
                          children={`$${c.price ?? "-"}`}
                          fontWeight={600}
                          fontSize={"14px"}
                          color="#273848"
                        />
                      </div>
                      <div className="col-6 col-md-1 text-end mt-2 mt-md-0">
                        <img
                          src={DeleteIcon}
                          alt="DeleteIcon"
                          className="delete-icon cursor-pointer"
                          style={{
                            height: "20px",
                            width: "20px",
                            opacity: 0.7,
                          }}
                          onMouseOver={(e) =>
                            (e.currentTarget.style.opacity = "1")
                          }
                          onMouseOut={(e) =>
                            (e.currentTarget.style.opacity = "0.7")
                          }
                          onClick={() => removeSelectedCampaign(c.productId)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="col mt-4">
              <Label
                children="Delivery Time"
                fontWeight={700}
                fontSize={"14.94px"}
                color="#273848"
              />
              <Label
                children="Delivery can take up to 2 working days"
                fontWeight={500}
                fontSize={"12.94px"}
                color="#273848"
              />
            </div>

            <div className="col mt-4">
              <div
                className="total-amount d-flex justify-content-between align-items-center px-4 py-3 rounded"
                style={{ backgroundColor: "#40E0D0" }}
              >
                <Label
                  children="Total amount"
                  fontWeight={600}
                  fontSize={"16px"}
                  color="#FFFFFF"
                />
                <Label
                  children={`$${campaignDisplayState.selectedCampaigns.reduce(
                    (sum: number, c: any) => sum + (Number(c.price) || 0),
                    0
                  )}`}
                  fontWeight={700}
                  fontSize={"20px"}
                  color="#FFFFFF"
                />
              </div>
            </div>
          </AccordionDetails>
        </Accordion>

        {/* Target Group Accordion */}
        <Accordion className="accordion mt-2">
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel2-content"
            id="panel2-header"
            className="accordion-header"
          >
            <Typography component="span" className="accortion-header-title">
              Target Group
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* Education */}
            <div className="col mt-2">
              <div className="d-flex align-items-center gap-1">
                <Label
                  children="Education"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                  className="mb-1"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
              </div>
              <div>
                <Autocomplete
                  value={campaignDisplayState.selectedEducation}
                  onChange={(event, newValue) =>
                    handleChange("selectedEducation", newValue)
                  }
                  options={education}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        campaignDisplayState.selectedEducation
                          ? ""
                          : "No education selected"
                      }
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </div>
            </div>

            {/* Seniority */}
            <div className="col mt-4">
              <div className="d-flex align-items-center gap-1">
                <Label
                  children="Seniority"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                  className="mb-1"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
              </div>
              <div>
                <Autocomplete
                  value={campaignDisplayState.selectedSeniority}
                  onChange={(event, newValue) =>
                    handleChange("selectedSeniority", newValue)
                  }
                  options={seniority}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        campaignDisplayState.selectedSeniority
                          ? ""
                          : "No seniority selected"
                      }
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </div>
            </div>

            {/* Job Function */}
            <div className="col mt-4">
              <div className="d-flex align-items-center gap-1">
                <Label
                  children="Job Function"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                  className="mb-1"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
              </div>
              <div>
                <Label
                  children="Job function for this position"
                  fontWeight={500}
                  fontSize={"14px"}
                  color="#4E4E4E"
                  className="mb-1"
                />
                <Autocomplete
                  value={campaignDisplayState.selectedJobFunction}
                  onChange={(event, newValue) =>
                    handleChange("selectedJobFunction", newValue)
                  }
                  options={jobFunctions}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        campaignDisplayState.selectedJobFunction
                          ? ""
                          : "No job function selected"
                      }
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </div>
            </div>

            {/* Industry */}
            <div className="col mt-4">
              <div className="d-flex align-items-center gap-1">
                <Label
                  children="Industry"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                  className="mb-1"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
              </div>
              <div>
                <Label
                  children="Industry for this position"
                  fontWeight={500}
                  fontSize={"14px"}
                  color="#4E4E4E"
                  className="mb-1"
                />
                <Autocomplete
                  value={campaignDisplayState.selectedIndustry}
                  onChange={(event, newValue) =>
                    handleChange("selectedIndustry", newValue)
                  }
                  options={industries}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        campaignDisplayState.selectedIndustry
                          ? ""
                          : "No industry selected"
                      }
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </div>
            </div>
          </AccordionDetails>
        </Accordion>

        {/* Recruiter Info & Contact Info Accordion */}
        <Accordion className="accordion mt-2">
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel3-content"
            id="panel3-header"
            className="accordion-header"
          >
            <Typography component="span" className="accortion-header-title">
              Recruiter Info & Contact Info
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div className="col d-flex align-items-start justify-content-center flex-column gap-1 mt-2">
              <Label
                children="Recruiter Information"
                fontSize={"18px"}
                fontWeight={700}
                color="#273848"
              />
            </div>

            {/* Recruiter Full Name */}
            <div className="col mt-3">
              <div className="d-flex align-items-center gap-1">
                <Label
                  children="Recruiter Full Name"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                  className="mb-1"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
              </div>
              <div>
                <TextField
                  value={campaignDisplayState.recruiterFullName || ""}
                  onChange={(e) =>
                    handleChange("recruiterFullName", e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </div>
            </div>

            {/* Recruiter Email */}
            <div className="col mt-4">
              <div className="d-flex align-items-center gap-1">
                <Label
                  children="Education"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                  className="mb-1"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
              </div>
              <div>
                <TextField
                  value={campaignDisplayState.recruiterEmail || ""}
                  onChange={(e) =>
                    handleChange("recruiterEmail", e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </div>
            </div>

            {/* Contact Info Section */}
            <div className="col mt-5">
              <Label
                children="Contact Information"
                fontWeight={700}
                fontSize={"18px"}
                color="#273848"
              />
              <Label
                children="Information for candidates to contact"
                fontWeight={500}
                fontSize={"12.94px"}
                color="#273848"
              />
            </div>

            {/* Contact Full Name */}
            <div className="col mt-3">
              <div className="d-flex align-items-center gap-1">
                <Label
                  children="Contact Full Name"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                  className="mb-1"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
              </div>
              <div>
                <TextField
                  value={campaignDisplayState.contactFullName || ""}
                  onChange={(e) =>
                    handleChange("contactFullName", e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </div>
            </div>

            {/* Contact Email */}
            <div className="col mt-4">
              <div className="d-flex align-items-center gap-1">
                <Label
                  children="Contact Email Address"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                  className="mb-1"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
              </div>
              <div>
                <TextField
                  value={campaignDisplayState.contactEmail || ""}
                  onChange={(e) => handleChange("contactEmail", e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </div>
            </div>

            {/* Contact Phone */}
            <div className="col mt-4">
              <Label
                children="Contact Phone Number"
                fontWeight={700}
                fontSize={"14.94px"}
                color="#273848"
              />
              <div>
                <TextField
                  value={campaignDisplayState.contactPhone || ""}
                  onChange={(e) => handleChange("contactPhone", e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="Enter contact phone number"
                />
              </div>
            </div>
          </AccordionDetails>
        </Accordion>

        {/* Posting Details & Working Location Accordion */}
        <Accordion className="accordion mt-2">
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel4-content"
            id="panel4-header"
            className="accordion-header"
          >
            <Typography component="span" className="accortion-header-title">
              Posting Details & Working Location
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* Campaign Name */}
            <div className="col mt-2">
              <Label
                children="Campaign Name"
                fontWeight={700}
                fontSize={"14.94px"}
                color="#273848"
              />
              <div>
                <TextField
                  value={campaignDisplayState.campaignName || ""}
                  onChange={(e) => handleChange("campaignName", e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </div>
            </div>

            {/* Job Title */}
            <div className="col mt-4">
              <div className="d-flex align-items-center gap-1">
                <Label
                  children="Job Title"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
              </div>
              <div>
                <TextField
                  value={campaignDisplayState.jobTitle || ""}
                  onChange={(e) => handleChange("jobTitle", e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </div>
            </div>

            {/* Job Description */}
            <div className="col mt-4">
              <div className="d-flex align-items-center gap-1">
                <Label
                  children="Job Description"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                  className="mb-1"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
              </div>
              {/* <TextField
                value={campaignDisplayState.jobDescription || ""}
                onChange={(e) => handleChange("jobDescription", e.target.value)}
                variant="outlined"
                multiline
                rows={10}
                fullWidth
              /> */}
              <ReactQuill
                theme="snow"
                value={campaignDisplayState.jobDescription || ""}
                onChange={(content) => handleChange("jobDescription", content)}
                style={{
                  direction: "ltr",
                  textAlign: "left",
                  height: "300px",
                  marginBottom: "50px",
                }}
              />
            </div>

            {/* Years of Experience */}
            <div className="col mt-4">
              <div className="d-flex align-items-center gap-1">
                <Label
                  children="Years of Experience"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                  className="mb-1"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
              </div>
              <div>
                <TextField
                  type="number"
                  value={campaignDisplayState.yearsOfExperience}
                  onChange={(e) =>
                    handleChange(
                      "yearsOfExperience",
                      e.target.value ? parseInt(e.target.value) : 0
                    )
                  }
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </div>
            </div>

            {/* Employment Type */}
            <div className="col mt-4">
              <div className="d-flex align-items-center gap-1">
                <Label
                  children="Employment Type"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                  className="mb-1"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
              </div>
              <TextField
                value={campaignDisplayState.employmentType || ""}
                onChange={(e) => handleChange("employmentType", e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
              />
            </div>

            {/* Working Hours */}
            <div className="col mt-4">
              <Label
                children="Weekly Working Hours"
                fontWeight={700}
                fontSize={"14.94px"}
                color="#273848"
              />
              <div className="d-flex flex-column flex-md-row gap-3">
                <div className="w-100">
                  <Label
                    children="From Hours"
                    fontWeight={500}
                    fontSize="12px"
                    color="#4E4E4E"
                  />
                  <TextField
                    value={campaignDisplayState.fromWorkingHours || ""}
                    onChange={(e) =>
                      handleChange("fromWorkingHours", e.target.value)
                    }
                    type="number"
                    inputProps={{ max: 168, min: 0 }}
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder="Enter from working hours"
                  />
                </div>
                <div className="w-100">
                  <Label
                    children="From Minutes"
                    fontWeight={500}
                    fontSize="12px"
                    color="#4E4E4E"
                  />
                  <TextField
                    value={campaignDisplayState.fromWorkingMinutes || ""}
                    onChange={(e) =>
                      handleChange("fromWorkingMinutes", e.target.value)
                    }
                    variant="outlined"
                    size="small"
                    inputProps={{ max: 168, min: 0 }}
                    fullWidth
                    placeholder="Enter from working minutes"
                  />
                </div>
                <div className="w-100">
                  <Label
                    children="To Hours"
                    fontWeight={500}
                    fontSize="12px"
                    color="#4E4E4E"
                  />
                  <TextField
                    value={campaignDisplayState.toWorkingHours || ""}
                    onChange={(e) =>
                      handleChange(
                        "toWorkingHours",
                        e.target.value ? Number(e.target.value) : 0
                      )
                    }
                    variant="outlined"
                    inputProps={{ max: 168, min: 0 }}
                    size="small"
                    fullWidth
                    placeholder="Enter to working hours"
                  />
                </div>
                <div className="w-100">
                  <Label
                    children="To Minutes"
                    fontWeight={500}
                    fontSize="12px"
                    color="#4E4E4E"
                  />
                  <TextField
                    value={campaignDisplayState.toWorkingMinutes || ""}
                    onChange={(e) =>
                      handleChange("toWorkingMinutes", e.target.value)
                    }
                    variant="outlined"
                    inputProps={{ max: 168, min: 0 }}
                    size="small"
                    fullWidth
                    placeholder="Enter to working minutes"
                  />
                </div>
              </div>
            </div>

            {/* Salary Information */}
            <div className="col mt-4">
              <Label
                children="Salary Indication"
                fontWeight={700}
                fontSize={"14.94px"}
                color="#273848"
              />
              <div className="d-flex flex-column flex-md-row gap-3">
                <div className="w-100">
                  <Label
                    children="From"
                    fontWeight={500}
                    fontSize="12px"
                    color="#4E4E4E"
                  />
                  <TextField
                    value={campaignDisplayState.salaryIndicationFrom || ""}
                    onChange={(e) =>
                      handleChange("salaryIndicationFrom", e.target.value)
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder="Enter from salary indication"
                  />
                </div>
                <div className="w-100">
                  <Label
                    children="To"
                    fontWeight={500}
                    fontSize="12px"
                    color="#4E4E4E"
                  />
                  <TextField
                    value={campaignDisplayState.salaryIndicationTo || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty string; no default value so we can send null in payload
                      handleChange("salaryIndicationTo", value);
                    }}
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder="Enter to salary indication"
                  />
                </div>
              </div>
            </div>

            {/* Working Location Section */}
            <div className="col d-flex align-items-start justify-content-center flex-column gap-1 mt-5">
              <Label
                children="Working Location"
                fontSize={"18px"}
                fontWeight={700}
                color="#273848"
              />
              <Label
                children="Location details for the position"
                fontWeight={500}
                fontSize={"12.94px"}
                color="#273848"
              />
            </div>

            {/* Address Line 1 */}
            <div className="col mt-3">
              <div className="d-flex align-items-center gap-1">
                <Label
                  children="Address Line 1"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                  className="mb-1"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
              </div>
              <TextField
                value={campaignDisplayState.addressLine1 || ""}
                onChange={(e) => handleChange("addressLine1", e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
              />
            </div>

            {/* Address Line 2 */}
            <div className="col mt-4">
              <div className="d-flex align-items-center gap-1">
                <Label
                  children="Address Line 2"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                  className="mb-1"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
              </div>
              <TextField
                value={campaignDisplayState.addressLine2 || ""}
                onChange={(e) => handleChange("addressLine2", e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Enter address line 2"
              />
            </div>

            {/* City, Postcode */}
            <div className="d-flex flex-column flex-md-row gap-3 mt-4">
              <div className="w-100">
                <div className="d-flex align-items-center gap-1">
                  <Label
                    children="City"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                    className="mb-1"
                  />
                  <Label
                    children="*"
                    fontWeight={500}
                    fontSize={"14.94px"}
                    color="#FF0000"
                  />
                </div>
                <TextField
                  value={campaignDisplayState.city || ""}
                  onChange={(e) => handleChange("city", e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </div>

              <div className="w-100">
                <div className="d-flex align-items-center gap-1">
                  <Label
                    children="Postcode"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                    className="mb-1"
                  />
                  <Label
                    children="*"
                    fontWeight={500}
                    fontSize={"14.94px"}
                    color="#FF0000"
                  />
                </div>
                <TextField
                  value={campaignDisplayState.postCode || ""}
                  onChange={(e) => handleChange("postCode", e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </div>
            </div>

            {/* Country */}
            <div className="col mt-4">
              <div className="d-flex align-items-center gap-1">
                <Label
                  children="Country"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                  className="mb-1"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
              </div>
              <TextField
                value={campaignDisplayState.country || ""}
                onChange={(e) => handleChange("country", e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
              />
            </div>

            {/* Remote Work */}
            <div className="col mt-4">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={campaignDisplayState.allowsRemoteWork}
                      onChange={(e) =>
                        handleChange("allowsRemoteWork", e.target.checked)
                      }
                      sx={{
                        color: "black",
                        "&.Mui-checked": {
                          color: "black",
                        },
                      }}
                    />
                  }
                  label="Allows remote work"
                  sx={{ color: "black" }}
                />
              </FormGroup>
            </div>
          </AccordionDetails>
        </Accordion>
      </div>

      <hr className="mt-5" />

      {/* Navigation Buttons */}
      <div className="col d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 pt-4">
        <button className="previous-btn w-100" onClick={prevStep}>
          {isMobile
            ? "Previous"
            : "Previous - Posting Details & Working Location"}
        </button>
        <button
          className="continue-btn w-100"
          disabled={submitting}
          onClick={handleSubmitValidatePayload}
        >
          {submitting
            ? "Submitting..."
            : isMobile
            ? "Order Campaign"
            : "Order Campaign"}
        </button>
      </div>
    </div>
  );
};

export default Form6;
